import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Email from "./Email";
import Total from "./QuoteTotal";
import COLORS from "../colors";
import { deepFreeze, timestampToDate } from "../util/misc";

export const CUSTOMER_QUOTE_RECEIVED_FIELDS = {
  date: null,
  recipients: [],
  nonfinal: false,
  update: false,
};
deepFreeze(CUSTOMER_QUOTE_RECEIVED_FIELDS);

export class CustomerQuoteReceived extends DataStructure {
  constructor(parent, data = {}) {
    // Backward compatibility
    data = { ...data };
    if ("email" in data && data.email) {
      data.recipients = [{ email: data.email }];
    }
    if ("protected" in data) {
      data.nonfinal = Boolean(data.protected);
    }

    data = sanitize(data, CUSTOMER_QUOTE_RECEIVED_FIELDS);

    // Convert to Date
    if (data.date) {
      data.date = timestampToDate(data.date);
    }

    super(data);
    this._parent = parent;
  }

  get recipients() {
    return this.data.recipients.map((recipient) => ({
      contact: store.state.main.contacts[recipient.contact] || null,
      email: new Email(recipient.email),
    }));
  }

  get status() {
    if (this._parent.data.response) {
      return "responded";
    } else if (this._parent._parent.expired) {
      return "expired";
    } else if (this._parent.data.received.date) {
      return "received";
    }
  }

  get statusType() {
    const type = CUSTOMER_QUOTE_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }
}

export const CUSTOMER_QUOTE_RESPONSE_STATUS_FIELDS = {
  submitted: null,
  isApproved: false,
  hasChanges: false,
  nonfinal: false,
  update: false,
  declinedReason: "",
  total: null,
};
deepFreeze(CUSTOMER_QUOTE_RESPONSE_STATUS_FIELDS);

export class CustomerQuoteResponseStatus extends DataStructure {
  constructor(data = {}) {
    data = sanitize(data, CUSTOMER_QUOTE_RESPONSE_STATUS_FIELDS);

    // Convert to Date
    data.submitted = timestampToDate(data.submitted);

    super(data);

    // Total
    this.data.total = this.total.data;
  }

  get date() {
    return this.data.submitted;
  }

  get status() {
    if (this.data.declinedReason) {
      return "declined";
    } else if (this.data.update) {
      return "update";
    } else if (this.data.isApproved) {
      if (this.data.nonfinal) {
        return "nonfinal";
      } else if (this.data.hasChanges) {
        return "approved-changes";
      } else {
        return "approved";
      }
    } else if (this.data.submitted && this.data.hasChanges) {
      return "declined-changes";
    } else {
      return "";
    }
  }

  get statusType() {
    const type = CUSTOMER_QUOTE_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get total() {
    return new Total(this.data.total);
  }
}

export const CUSTOMER_QUOTE_STATUS_FIELDS = {
  type: "",
  received: {},
  response: null,
};
deepFreeze(CUSTOMER_QUOTE_STATUS_FIELDS);

export const CUSTOMER_QUOTE_STATUS_TYPES = {
  received: {
    order: 1,
    label: "Received",
    icon: "received",
    color: "warning",
  },
  responded: {
    order: 2,
    label: "Received",
    icon: "received",
    color: "positive",
  },
  nonfinal: {
    order: 2,
    label: "Final quote requested",
    icon: "done",
    color: "positive",
  },
  "approved-changes": {
    order: 3,
    label: "Approved with changes",
    icon: "done",
    color: "warning",
  },
  approved: {
    order: 4,
    label: "Approved as-is",
    icon: "done",
    color: "positive",
  },
  "declined-changes": {
    order: 5,
    label: "Declined with changes",
    icon: "decline",
    color: "warning",
  },
  declined: {
    order: 6,
    label: "Declined",
    icon: "decline",
    color: "negative",
  },
  expired: {
    order: 7,
    label: "Expired",
    icon: "pending",
    color: "negative",
  },
  update: {
    order: 8,
    label: "Updated quote requested",
    icon: "refresh",
    color: "positive",
  },
};
deepFreeze(CUSTOMER_QUOTE_STATUS_TYPES);

export default class CustomerQuoteStatus extends DataStructure {
  constructor(parent, data = {}) {
    data = sanitize(data, CUSTOMER_QUOTE_STATUS_FIELDS);

    super(data);
    this._parent = parent;

    if (this.data.received) {
      this.data.received = this.received.data;
    }

    if (this.data.response) {
      this.data.response = this.response.data;
    }

    if (!this.data.type) {
      this.data.type = this.get();
    }
  }

  get() {
    if (this.data.response) {
      return this.response.status;
    } else {
      return this.received.status;
    }
  }

  get date() {
    if (this.data.response) {
      return this.response.date;
    } else {
      return this.received.date;
    }
  }

  get reason() {
    const response = this.response;
    return response ? response.declinedReason : "";
  }

  // Formatting

  get type() {
    const type = CUSTOMER_QUOTE_STATUS_TYPES[this.data.type];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get received() {
    return new CustomerQuoteReceived(this, this.data.received);
  }

  get response() {
    return this.data.response
      ? new CustomerQuoteResponseStatus(this.data.response)
      : null;
  }
}
